<template>
  <div>
    <div class="item-summary-row">
      <div class="item-summary-cell">
        <img class="icon" v-bind:src="'/app/images/icons/sources/'+source.config.fileType+'.svg'" />
        <strong>{{ source.name }}</strong>
      </div>
      <div class="item-summary-cell">
        <div v-if="source.config.fileType !== 'analytics'">
          <div v-if="source.config.fileType === 'docs'" class="d-flex open-file-copy-link-container">
            <a v-if="source.config.docsConfig !== undefined" v-bind:href="'https://docs.google.com/spreadsheets/d/'+source.config.docsConfig.docsKey" target="_blank" class="mr-4 pr-3">
              <link-icon></link-icon>
              <span class="ml-2">{{ $t('sources.open_file') }}</span>
            </a>
            <div :id="'copy-feed-url-'+source.id" class="d-flex align-items-center cursor-pointer copy-link-container" v-clipboard:copy="'https://docs.google.com/spreadsheets/d/'+source.config.docsConfig.docsKey" v-clipboard:success="onCopy('copy-feed-url-'+source.id)">
              <copy-icon></copy-icon>
              <p class="ml-2 mb-1">{{ $t('feeds.copy_link') }}</p>
            </div>
            <b-popover :target="'copy-feed-url-'+source.id" variant="success" triggers="click" placement="bottomleft">
              <div>
                <p class="mb-0">{{ $t('feeds.url_copied_to_clipboard') }}</p>
              </div>
            </b-popover>
          </div>
          <div v-else class="d-flex mr-4 open-file-copy-link-container">
            <div class="d-flex cursor-pointer">
              <a v-bind:href="source.config.url" target="_blank" class="mr-4 pr-3">
                <link-icon></link-icon>
                <span class="ml-2">{{ $t('sources.open_file') }}</span>
              </a>
            </div>
            <div :id="'copy-feed-url-'+source.id" class="d-flex align-items-center cursor-pointer copy-link-container" v-clipboard:copy="source.config.url" v-clipboard:success="onCopy('copy-feed-url-'+source.id)">
              <copy-icon></copy-icon>
              <p class="ml-2 mb-1">{{ $t('feeds.copy_link') }}</p>
            </div>
            <b-popover :target="'copy-feed-url-'+source.id" variant="success" triggers="click" placement="bottomleft">
              <div>
                <p class="mb-0">{{ $t('feeds.url_copied_to_clipboard') }}</p>
              </div>
            </b-popover>
          </div>
        </div>
        <div v-else>
          <strong>{{ source.config.analyticsConfig.profileId }}</strong>
        </div>
      </div>
      <div class="item-summary-cell">
        <div v-if="this.hasAlerts">
          <b-popover :target="'tooltip-source-alerts'+source.id" variant="danger" triggers="hover" placement="bottomleft">
            <div>
              <p class="mb-0">{{ alerts.source.filter(alert => alert.sourceId === source.id)[0].message }}</p>
            </div>
          </b-popover>
          <img class="icon cursor-pointer" :id="'tooltip-source-alerts'+source.id" src="/app/images/icons/error.svg" />
        </div>
        <div v-else>
          <img class="icon" src="/app/images/icons/success.svg" />
        </div>
        <div>
          <p class="mb-2"><strong>{{ source.lastProductCount }} {{ $t('global.products') }}</strong></p>
          <p class="mb-0">{{ countHoursAgo(source.lastUpdate, new Date()) }} {{ $t('global.hours_ago') }}</p>
        </div>
      </div>
      <div class="item-summary-cell justify-content-center">
        <img v-if="source.config.primary" id="mainSourceFileIcon" class="icon mx-4 align-self-center" src="/app/images/icons/primary.png" />
        <b-tooltip target="mainSourceFileIcon" triggers="hover">{{ $t('sources.main_file') }}</b-tooltip>
      </div>
      <div class="item-summary-cell toggle-button-container justify-content-end">
        <button @click="toggleSource" class="btn btn-gray btn-toggle">
          <img v-bind:class="[isHidden ? '' : 'rotated']" src="/app/images/icons/toggle.svg">
        </button>
      </div>
    </div>
    <transition name="slide-down">
      <div class="item-summary-row" v-show="!isHidden">
        <div class="item-summary-cell flex-column align-items-start">
          <div class="d-flex w-100">
            <div class="w-50"><p>{{ $t('sources.added') }}</p></div>
            <div class="w-50"><p class="text-green">{{ source.addDate.substr(0, 10) }}</p></div>
          </div>
          <div class="d-flex w-100 mb-4">
            <div class="w-50"><p>{{ $t('projects.last_update') }}</p></div>
            <div class="w-50"><p class="mb-4"> <span class="text-green">{{ countHoursAgo(source.lastUpdate, new Date()) }} {{ $t('global.hours_ago') }}</span></p></div>
          </div>
          <div class="d-flex">
            <router-link :to="{ name: 'source.edit', params: { projectId: projectId, sourceId: source.id }}">
              <button type="button" class="btn btn-small btn-orange">
                <settings-icon></settings-icon>
                <span>{{ $t('global.settings') }}</span>
              </button>
            </router-link>
            <button @click="previewSource" type="button" class="btn btn-small btn-orange ml-4">
              <eye-icon></eye-icon>
              <span>{{ $t('global.preview') }}</span>
            </button>
          </div>
        </div>
        <div class="item-summary-cell"></div>
        <div class="item-summary-cell"></div>
        <div class="item-summary-cell"></div>
        <div class="item-summary-cell project-source-toggle">
          <button v-if="source.active" type="button" class="btn btn-small btn-gray-dark" @click="editSourceStatus">
            <pause-circle-icon></pause-circle-icon>
            <span>{{ $t('projects.deactivate') }}</span>
          </button>
          <button v-else type="button" class="btn btn-small btn-orange-dark" @click="editSourceStatus">
            <play-circle-icon></play-circle-icon>
            <span>{{ $t('projects.activate') }}</span>
          </button>
          <button class="btn btn-small btn-danger ml-4" @click="removeSource(source.id)">
            <trash-2-icon></trash-2-icon>
            <span>{{ $t('global.delete') }}</span>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import dates from '@/mixins/dates'
import { CopyIcon, EyeIcon, LinkIcon, PauseCircleIcon, PlayCircleIcon, SettingsIcon, Trash2Icon } from 'vue-feather-icons'

export default {
  name: 'SourceSummary',

  data () {
    return {
      isHidden: true
    }
  },

  props: {
    alerts: { required: true, type: Object },
    hasAlerts: { required: true, type: Boolean },
    projectId: { required: true, type: Number },
    source: { required: true, type: Object }
  },

  mixins: [dates],

  methods: {
    toggleSource () {
      this.isHidden = !this.isHidden
    },

    editSourceStatus () {
      this.source.active = !this.source.active
      this.$emit('onSourceStatusUpdate', this.source)
    },

    removeSource () {
      this.$emit('onSourceRemove', this.source)
    },

    previewSource () {
      this.$emit('onSourcePreview', this.source)
    },

    onCopy (popoverId) {
      setTimeout(function () {
        this.$root.$emit('bv::hide::popover', popoverId)
      }.bind(this), 1500)
    }
  },

  components: { CopyIcon, EyeIcon, LinkIcon, PauseCircleIcon, PlayCircleIcon, SettingsIcon, Trash2Icon }
}
</script>
